import React, { useEffect, useState, useCallback } from 'react';
import { Card, Table, Button, Input, Modal, notification, Tag, Form, Space } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, CalendarOutlined } from '@ant-design/icons';
import axios from 'axios';
import { baseUrl } from '../../config';

const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('accessToken')}` });

const PRESETS = [
  { name: 'दीपावली',           emojis: '🪔,✨,🎆,🎇,🌟,💫,🪔,✨', bg1: '#7a0000', bg2: '#c0392b', tc: '#FFD700', msg: 'दीपावली के इस पावन पर्व पर श्री खेतेश्वर महाराज जी की कृपा आप पर सदा बनी रहे। 🙏' },
  { name: 'होली',              emojis: '🎨,🌈,💧,🎉,🌸,🎊,🎨,🌈', bg1: '#7c3aed', bg2: '#db2777', tc: '#fff',    msg: 'रंगों का यह त्योहार आपके जीवन में खुशियों के रंग भरे। होली की हार्दिक शुभकामनाएं! 🌈' },
  { name: 'नवरात्रि',          emojis: '🪔,🌺,🎊,✨,🌸,🎉,🪔,🌺', bg1: '#b91c1c', bg2: '#991b1b', tc: '#FFD700', msg: 'नवरात्रि के पावन पर्व पर माँ दुर्गा का आशीर्वाद आप पर बरसे। 🙏' },
  { name: 'दशहरा / विजयदशमी', emojis: '🏹,🎊,✨,🌸,🎉,🙏,🏹,🎊', bg1: '#d97706', bg2: '#b45309', tc: '#fff',    msg: 'असत्य पर सत्य की विजय का यह पर्व आपके जीवन में भी विजय लाए। 🙏' },
  { name: 'मकर संक्रांति',     emojis: '🪁,☀️,🌾,🎉,🌸,✨,🪁,☀️', bg1: '#7a0000', bg2: '#243d60', tc: '#FFD700', msg: 'मकर संक्रांति की हार्दिक शुभकामनाएं! सूर्य देव आपके जीवन में उजाला लाएं। 🌞' },
  { name: 'गुरु पूर्णिमा',     emojis: '🙏,🌕,✨,🌸,🌺,💫,🙏,🌕', bg1: '#7a0000', bg2: '#b71c1c', tc: '#FFD700', msg: 'गुरु पूर्णिमा पर श्री खेतेश्वर महाराज जी के श्री चरणों में कोटि-कोटि प्रणाम। 🙏' },
];

export default function FestivalList() {
  const [data,    setData]    = useState([]);
  const [loading, setLoading] = useState(false);
  const [modal,   setModal]   = useState(false);
  const [editing, setEditing] = useState(null);
  const [form]                = Form.useForm();

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${baseUrl}/admin/festivals`, { headers: auth() });
      if (r.data.status) setData(r.data.result || []);
    } catch { notification.error({ message: 'Failed to load', placement: 'topRight' }); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  const openNew = (preset = null) => {
    setEditing(null);
    form.resetFields();
    if (preset) {
      form.setFieldsValue({
        name: preset.name, emojis: preset.emojis,
        bg_color_1: preset.bg1, bg_color_2: preset.bg2,
        text_color: preset.tc, greeting_message: preset.msg,
      });
    }
    setModal(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    form.setFieldsValue({
      name:             row.name,
      celebration_date: row.celebration_date?.slice(0, 10) || '',
      emojis:           row.emojis,
      bg_color_1:       row.bg_color_1,
      bg_color_2:       row.bg_color_2,
      text_color:       row.text_color,
      greeting_message: row.greeting_message,
      is_active:        !!row.is_active,
    });
    setModal(true);
  };

  const save = async () => {
    try {
      const vals = await form.validateFields();
      if (editing) {
        await axios.patch(`${baseUrl}/admin/festivals/${editing.id}`, vals, { headers: auth() });
      } else {
        await axios.post(`${baseUrl}/admin/festivals`, vals, { headers: auth() });
      }
      notification.success({ message: editing ? 'Updated ✅' : 'Created ✅', placement: 'topRight' });
      setModal(false);
      fetch();
    } catch (err) {
      if (err?.response?.data?.message) notification.error({ message: err.response.data.message, placement: 'topRight' });
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this festival?')) return;
    await axios.delete(`${baseUrl}/admin/festivals/${id}`, { headers: auth() });
    notification.success({ message: 'Deleted', placement: 'topRight' });
    fetch();
  };

  const columns = [
    { title: 'Festival', dataIndex: 'name', render: (v, r) => <><span style={{ fontSize: 18 }}>{r.emojis?.split(',')[0]}</span> <b style={{ marginLeft: 6 }}>{v}</b></> },
    { title: 'Date',   dataIndex: 'celebration_date', render: v => v?.slice(0, 10) },
    { title: 'Status', dataIndex: 'is_active',        render: v => <Tag color={v ? 'green' : 'default'}>{v ? 'Active' : 'Inactive'}</Tag> },
    { title: 'Actions', render: (_, r) => (
      <Space>
        <Button size="small" icon={<EditOutlined />}  onClick={() => openEdit(r)} />
        <Button size="small" danger icon={<DeleteOutlined />} onClick={() => remove(r.id)} />
      </Space>
    )},
  ];

  return (
    <div style={{ padding: '12px 0' }}>
      <Card
        title={<><CalendarOutlined style={{ color: '#7a0000', marginRight: 8 }} />Festival Celebrations</>}
        extra={<Button type="primary" icon={<PlusOutlined />} onClick={() => openNew()} style={{ background: '#7a0000' }}>Add Festival</Button>}
      >
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 8, fontWeight: 600 }}>Quick Add Presets:</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {PRESETS.map(p => (
              <Button key={p.name} size="small" onClick={() => openNew(p)}>{p.emojis.split(',')[0]} {p.name}</Button>
            ))}
          </div>
        </div>
        <Table dataSource={data} columns={columns} rowKey="id" loading={loading} size="small" />
      </Card>

      <Modal open={modal} title={editing ? 'Edit Festival' : 'Add Festival'} onOk={save} onCancel={() => setModal(false)} width={500}>
        <Form form={form} layout="vertical">
          <Form.Item name="name"             label="Festival Name"            rules={[{ required: true }]}><Input /></Form.Item>
          <Form.Item name="celebration_date" label="Date (YYYY-MM-DD)"        rules={[{ required: true }]}><Input type="date" style={{ width: '100%' }} /></Form.Item>
          <Form.Item name="emojis"           label="Emojis (comma-separated)" rules={[{ required: true }]}><Input placeholder="🎊,🎉,✨,🌸" /></Form.Item>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
            <Form.Item name="bg_color_1" label="BG Color 1"><Input type="color" /></Form.Item>
            <Form.Item name="bg_color_2" label="BG Color 2"><Input type="color" /></Form.Item>
            <Form.Item name="text_color" label="Text Color"><Input type="color" /></Form.Item>
          </div>
          <Form.Item name="greeting_message" label="Greeting Message"><Input.TextArea rows={3} /></Form.Item>
          <Form.Item name="is_active" label="Active" valuePropName="checked"><input type="checkbox" /></Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
